import React from 'react';
import {
  Card,
  CardContent,
  IconButton,
  Typography,
  Box,
  Chip,
  Stack,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Rule } from './types';
import { GiftRuleType } from '../../../../models';

interface RuleCardProps {
  rule: Rule;
  onRemove: () => void;
  fuelNames?: string[];
  stationNames: string[];
}

export const RuleCard: React.FC<RuleCardProps> = ({
  rule,
  onRemove,
  fuelNames,
  stationNames,
}) => {
  const ruleTypeName = GiftRuleType.giftRuleTypeChoices.find(
    (choice) => choice.id === rule.type
  )?.name || rule.type;

  const isSaleRule = GiftRuleType.isSaleType(rule.type);
  // Só as regras dirigidas por evento usam gift_rule_place
  const showsPlaces = GiftRuleType.isEventType(rule.type);
  // Compra vale em posto e em parceiro; abastecimento, só em posto
  const placesLabel = isSaleRule ? 'Locais' : 'Postos';
  const quantity = rule.quantity ?? 1;

  return (
    <Card
      sx={{
        mb: 1.5,
        backgroundColor: '#fff',
        border: '1px solid #e0e0e0',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        '&:hover': {
          boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
        },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Typography variant="subtitle2" fontWeight={600} color="primary">
            {ruleTypeName}
          </Typography>
          <IconButton
            size="small"
            onClick={onRemove}
            sx={{
              color: 'error.main',
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.08)',
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>

        <Stack spacing={0.5}>
          {rule.type === GiftRuleType.clube_certo ? (
            <>
              {/* Mostrar apenas Período de Verificação para Clube Certo */}
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Período de Verificação:
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {rule.clubeCertoCheckPeriodDays || 30} dias
                </Typography>
              </Box>
            </>
          ) : rule.type === GiftRuleType.pix_payment ? (
            <>
              {/* Mostrar valor do pagamento para Pix Payment */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#e8f5e9',
                  borderRadius: 1,
                  py: 1.5,
                  px: 2,
                  mb: 1,
                }}
              >
                <Typography variant="h5" fontWeight={700} color="success.dark">
                  R$ {(rule.value || 0).toFixed(2).replace('.', ',')}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                Cliente paga este valor via Pix para adquirir as recompensas do brinde
              </Typography>
            </>
          ) : rule.type === GiftRuleType.points_exchange ? (
            <>
              {/* Preço da troca, em PONTOS — não é dinheiro, não leva R$ nem centavos */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#e0f2f1',
                  borderRadius: 1,
                  py: 1.5,
                  px: 2,
                  mb: 1,
                }}
              >
                <Typography variant="h5" fontWeight={700} sx={{ color: '#00695c' }}>
                  {(rule.value || 0).toLocaleString('pt-BR')} pontos
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                Cliente paga estes pontos para receber os números da sorte do brinde
              </Typography>
            </>
          ) : rule.type === GiftRuleType.prediction_winner ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: '#ede9fe',
                  borderRadius: 1,
                  py: 1.5,
                  px: 2,
                  mb: 1,
                }}
              >
                <Typography variant="h6" fontWeight={700} color="#7869DF">
                  ⚽ {rule.bolaoName || 'Bolão não selecionado'}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
                Entregue ao cliente que acertar o placar exato — processado manualmente no painel do bolão.
              </Typography>
            </>
          ) : rule.type === GiftRuleType.unique_code ? (
            <>
              {/* Código só aparece quando digitação está ativa e há código cadastrado */}
              {(rule.useManualCode !== false) && rule.code && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#e3f2fd',
                    borderRadius: 1,
                    py: 1.5,
                    px: 2,
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    color="primary.dark"
                    sx={{ fontFamily: 'monospace', letterSpacing: '2px' }}
                  >
                    {rule.code}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                {(rule.useManualCode !== false) && (
                  <Chip label="Digitação" size="small" color="primary" variant="outlined" />
                )}
                {rule.useQrCode && (
                  <Chip label="QR Code" size="small" color="primary" variant="outlined" />
                )}
                {rule.requireLocation && (
                  <Chip label="GPS obrigatório" size="small" color="warning" variant="outlined" />
                )}
              </Box>
            </>
          ) : GiftRuleType.isRegistrationType(rule.type) ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
              {rule.type === GiftRuleType.customer_registered
                ? 'Entregue uma única vez, quando o cliente confirma o cadastro no aplicativo.'
                : 'Entregue uma única vez, quando o cliente completa os dados do perfil.'}
            </Typography>
          ) : GiftRuleType.isSaleType(rule.type) ? (
            <>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="caption" color="text.secondary">
                  {rule.type === GiftRuleType.accumulated_sale ? 'Valor acumulado:' : 'Valor mínimo:'}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {rule.value > 0 ? `R$ ${rule.value.toFixed(2)}` : 'Qualquer valor'}
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Produtos:
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {(rule.saleProductNames || []).length > 0
                    ? (rule.saleProductNames || []).map(p => p.name).join(', ')
                    : (rule.productNames || []).length > 0
                      ? (rule.productNames || []).map(p => p.name).join(', ')
                      : 'Todos os produtos'}
                </Typography>
              </Box>

              {rule.type !== GiftRuleType.accumulated_sale && (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    Quantidade:
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {quantity} {quantity === 1 ? 'compra' : 'compras'}
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Combustível:
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {fuelNames && fuelNames.length > 0 ? fuelNames.join(', ') : 'Todos'}
                </Typography>
              </Box>

              {rule.type === GiftRuleType.accumulated_fillin ? (
                <>
                  {/* Mostrar campos específicos para Abastecimentos Acumulados */}
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      Limite Acumulado:
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {rule.valueType === 'cents'
                        ? `R$ ${rule.value.toFixed(2)}`
                        : `${rule.value.toFixed(2)} L`}
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  {/* Mostrar campos específicos para Abastecimento Único */}
                  {rule.value > 0 && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        {rule.valueType === 'cents' ? 'Valor mínimo:' : 'Litros mínimos:'}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {rule.valueType === 'cents'
                          ? `R$ ${rule.value.toFixed(2)}`
                          : `${rule.value.toFixed(2)} L`}
                      </Typography>
                    </Box>
                  )}

                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      Quantidade:
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {quantity} {quantity === 1 ? 'abastecimento' : 'abastecimentos'}
                    </Typography>
                  </Box>
                </>
              )}
            </>
          )}

          {/* Locais — comum às regras dirigidas por evento (abastecimento e
              compra), que são as que usam gift_rule_place. As especiais
              (clube_certo, pix, unique_code, bolão) não têm local. */}
          {showsPlaces && (
            <Box mt={1}>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                {placesLabel} ({stationNames.length}):
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {stationNames.slice(0, 3).map((name, idx) => (
                  <Chip
                    key={idx}
                    label={name}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                ))}
                {stationNames.length > 3 && (
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant="caption" fontWeight={600} display="block" mb={0.5}>
                          Demais {placesLabel.toLowerCase()}:
                        </Typography>
                        {stationNames.slice(3).map((name, idx) => (
                          <Typography key={idx} variant="caption" display="block">
                            • {name}
                          </Typography>
                        ))}
                      </Box>
                    }
                    arrow
                  >
                    <Chip
                      label={`+${stationNames.length - 3}`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 20, cursor: 'pointer' }}
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};
